// Cadastros do estoque: produtos (unidade padrão, mínimo, máximo, consumo planejado),
// categorias, unidades e fornecedores. Nada é excluído — só inativado (o histórico continua
// apontando para o cadastro). Permissões espelham backend/routers/estoque.py:
//   produto: criar = operador, editar = financeiro · fornecedor = financeiro
//   categoria/unidade = admin
import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search } from "lucide-react";
import {
  getEstoqueCategorias, getEstoqueFornecedores, getEstoqueProdutos, getEstoqueUnidades, getRoleFromToken,
  postEstoqueCategoria, postEstoqueFornecedor, postEstoqueProduto, postEstoqueUnidade,
  putEstoqueCategoria, putEstoqueFornecedor, putEstoqueProduto, putEstoqueUnidade,
} from "../../api";
import { permissoesEstoque } from "../../constants/estoque";
import EstoqueLayout from "./EstoqueLayout";
import {
  AvisoErro, BadgeAtivo, Campo, Modal, NumeroInput, btnStyle, formatQtd, inputStyle, numeroParaInput,
  palette, parseNumero, useIsMobile,
} from "./ui";

const ABAS = [
  { id: "produtos", label: "Produtos" },
  { id: "categorias", label: "Categorias" },
  { id: "unidades", label: "Unidades" },
  { id: "fornecedores", label: "Fornecedores" },
];

function formatCnpj(cnpj) {
  const d = (cnpj || "").replace(/\D/g, "");
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function mascaraCnpj(valor) {
  const d = valor.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

function thStyle(C, align = "left") {
  return {
    padding: "10px 12px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase",
    letterSpacing: "0.04em", textAlign: align, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    background: C.card, position: "sticky", top: 0,
  };
}

function tdStyle(C, align = "left") {
  return { padding: "10px 12px", fontSize: 13, color: C.text, textAlign: align, borderBottom: `1px solid ${C.border}` };
}

function Tabela({ C, colunas, linhas, vazio }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>{colunas.map((c) => <th key={c.id} style={thStyle(C, c.align)}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr><td colSpan={colunas.length} style={{ ...tdStyle(C, "center"), color: C.muted, padding: 24 }}>{vazio}</td></tr>
          )}
          {linhas.map((linha) => (
            <tr key={linha.key} style={{ opacity: linha.inativo ? 0.55 : 1 }}>
              {colunas.map((c) => <td key={c.id} style={tdStyle(C, c.align)}>{linha[c.id]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CartaoMobile({ C, titulo, subtitulo, inativo, detalhes, acao }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, opacity: inativo ? 0.6 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{titulo}</div>
          {subtitulo && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{subtitulo}</div>}
        </div>
        {acao}
      </div>
      {detalhes && <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 8, fontSize: 12, color: C.muted }}>{detalhes}</div>}
    </div>
  );
}

function BotaoEditar({ C, onClick }) {
  return (
    <button onClick={onClick} aria-label="Editar" style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, flexShrink: 0 }}>
      <Pencil size={15} />
    </button>
  );
}

// ─── Produtos ─────────────────────────────────────────────────────────────────

export function ModalProduto({ C, produto, categorias, unidades, podeEditarLimites, onClose, onSalvo }) {
  const novo = !produto;
  const [form, setForm] = useState(() => ({
    nome: produto?.nome ?? "",
    categoria_id: produto?.categoria_id ?? "",
    unidade_padrao_id: produto?.unidade_padrao_id ?? "",
    estoque_minimo: numeroParaInput(produto?.estoque_minimo),
    estoque_maximo: numeroParaInput(produto?.estoque_maximo),
    consumo_planejado_mensal: numeroParaInput(produto?.consumo_planejado_mensal),
    observacao: produto?.observacao ?? "",
    ativo: produto?.ativo ?? true,
  }));
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const set = (campo) => (valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const siglaUnidade = unidades.find((u) => u.id === Number(form.unidade_padrao_id))?.sigla;

  async function salvar() {
    setErro("");
    if (!form.nome.trim() || !form.categoria_id || !form.unidade_padrao_id) {
      setErro("Preencha nome, categoria e unidade padrão.");
      return;
    }
    const numericos = ["estoque_minimo", "estoque_maximo", "consumo_planejado_mensal"];
    if (numericos.some((c) => String(form[c]).trim() && parseNumero(form[c]) === null)) {
      setErro("Mínimo, máximo e consumo planejado aceitam só números (ex.: 12 ou 12,5).");
      return;
    }
    const minimo = parseNumero(form.estoque_minimo);
    const maximo = parseNumero(form.estoque_maximo);
    if (minimo !== null && maximo !== null && Number(minimo) > Number(maximo)) {
      setErro("Estoque mínimo não pode ser maior que o máximo.");
      return;
    }
    const dados = {
      nome: form.nome.trim(),
      categoria_id: Number(form.categoria_id),
      unidade_padrao_id: Number(form.unidade_padrao_id),
      observacao: form.observacao.trim() || null,
      ativo: form.ativo,
    };
    if (podeEditarLimites) {
      dados.estoque_minimo = minimo;
      dados.estoque_maximo = maximo;
      dados.consumo_planejado_mensal = parseNumero(form.consumo_planejado_mensal);
    }
    setSalvando(true);
    try {
      const salvo = novo ? await postEstoqueProduto(dados) : await putEstoqueProduto(produto.id, dados);
      onSalvo(salvo);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  const grid2 = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 };
  return (
    <Modal
      C={C}
      titulo={novo ? "Novo produto" : "Editar produto"}
      onClose={onClose}
      rodape={
        <>
          <button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button>
          <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.brand), opacity: salvando ? 0.6 : 1 }}>
            {salvando ? "Salvando..." : "Salvar"}
          </button>
        </>
      }
    >
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Campo label="Nome" C={C} obrigatorio>
          <input value={form.nome} onChange={(e) => set("nome")(e.target.value)} style={inputStyle(C)} autoFocus />
        </Campo>
        <div style={grid2}>
          <Campo label="Categoria" C={C} obrigatorio>
            <select value={form.categoria_id} onChange={(e) => set("categoria_id")(e.target.value)} style={inputStyle(C)}>
              <option value="">Selecione</option>
              {categorias.filter((c) => c.ativo || c.id === produto?.categoria_id).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </Campo>
          <Campo
            label="Unidade padrão"
            C={C}
            obrigatorio
            ajuda={produto?.tem_movimentacao ? "Travada: o produto já tem contagem/movimentação." : "Toda quantidade do produto é guardada nesta unidade."}
          >
            <select value={form.unidade_padrao_id} onChange={(e) => set("unidade_padrao_id")(e.target.value)} disabled={produto?.tem_movimentacao} style={{ ...inputStyle(C), opacity: produto?.tem_movimentacao ? 0.6 : 1 }}>
              <option value="">Selecione</option>
              {unidades.filter((u) => u.ativo || u.id === produto?.unidade_padrao_id).map((u) => <option key={u.id} value={u.id}>{u.sigla} — {u.descricao}</option>)}
            </select>
          </Campo>
        </div>
        {podeEditarLimites && (
          <div style={grid2}>
            <Campo label={`Estoque mínimo${siglaUnidade ? ` (${siglaUnidade})` : ""}`} C={C}>
              <NumeroInput C={C} value={form.estoque_minimo} onChange={set("estoque_minimo")} />
            </Campo>
            <Campo label={`Estoque máximo${siglaUnidade ? ` (${siglaUnidade})` : ""}`} C={C}>
              <NumeroInput C={C} value={form.estoque_maximo} onChange={set("estoque_maximo")} />
            </Campo>
            <Campo label={`Consumo planejado/mês${siglaUnidade ? ` (${siglaUnidade})` : ""}`} C={C}>
              <NumeroInput C={C} value={form.consumo_planejado_mensal} onChange={set("consumo_planejado_mensal")} />
            </Campo>
          </div>
        )}
        <Campo label="Observação" C={C}>
          <textarea value={form.observacao} onChange={(e) => set("observacao")(e.target.value)} rows={2} style={{ ...inputStyle(C), resize: "vertical" }} />
        </Campo>
        {!novo && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={form.ativo} onChange={(e) => set("ativo")(e.target.checked)} style={{ width: 18, height: 18 }} />
            Produto ativo (inativos somem das telas de lançamento)
          </label>
        )}
      </div>
    </Modal>
  );
}

function AbaProdutos({ C, isMobile, perm, categorias, unidades, produtos, recarregar }) {
  const [busca, setBusca] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("");
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [modal, setModal] = useState(null); // null | "novo" | produto

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return produtos.filter((p) =>
      (mostrarInativos || p.ativo) &&
      (!categoriaFiltro || p.categoria_id === Number(categoriaFiltro)) &&
      (!termo || p.nome.toLowerCase().includes(termo))
    );
  }, [produtos, busca, categoriaFiltro, mostrarInativos]);

  const colunas = [
    { id: "nome", label: "Produto" },
    { id: "categoria", label: "Categoria" },
    { id: "unidade", label: "Unidade" },
    { id: "minimo", label: "Mínimo", align: "right" },
    { id: "maximo", label: "Máximo", align: "right" },
    { id: "consumo", label: "Consumo plan./mês", align: "right" },
    { id: "status", label: "Status" },
    ...(perm.financeiro ? [{ id: "acoes", label: "", align: "right" }] : []),
  ];

  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 220px" }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.muted }} />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto" style={{ ...inputStyle(C), paddingLeft: 34 }} />
        </div>
        <select value={categoriaFiltro} onChange={(e) => setCategoriaFiltro(e.target.value)} style={{ ...inputStyle(C), flex: "0 1 180px", width: "auto" }}>
          <option value="">Todas as categorias</option>
          {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, cursor: "pointer" }}>
          <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} /> Mostrar inativos
        </label>
        {perm.operador && (
          <button onClick={() => setModal("novo")} style={{ ...btnStyle(C.brand), marginLeft: "auto" }}>
            <Plus size={15} /> Novo produto
          </button>
        )}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{filtrados.length} produto(s)</div>

      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtrados.map((p) => (
            <CartaoMobile
              key={p.id}
              C={C}
              titulo={p.nome}
              subtitulo={`${p.categoria_nome} · ${p.unidade_padrao_sigla}`}
              inativo={!p.ativo}
              acao={perm.financeiro && <BotaoEditar C={C} onClick={() => setModal(p)} />}
              detalhes={
                <>
                  <span>Mín.: {formatQtd(p.estoque_minimo)}</span>
                  <span>Máx.: {formatQtd(p.estoque_maximo)}</span>
                  <span>Plan./mês: {formatQtd(p.consumo_planejado_mensal)}</span>
                  {!p.ativo && <BadgeAtivo ativo={false} C={C} />}
                </>
              }
            />
          ))}
        </div>
      ) : (
        <Tabela
          C={C}
          colunas={colunas}
          vazio="Nenhum produto encontrado."
          linhas={filtrados.map((p) => ({
            key: p.id,
            inativo: !p.ativo,
            nome: <span style={{ fontWeight: 600 }}>{p.nome}</span>,
            categoria: p.categoria_nome,
            unidade: p.unidade_padrao_sigla,
            minimo: formatQtd(p.estoque_minimo),
            maximo: formatQtd(p.estoque_maximo),
            consumo: formatQtd(p.consumo_planejado_mensal),
            status: <BadgeAtivo ativo={p.ativo} C={C} />,
            acoes: <div style={{ display: "flex", justifyContent: "flex-end" }}><BotaoEditar C={C} onClick={() => setModal(p)} /></div>,
          }))}
        />
      )}

      {modal && (
        <ModalProduto
          C={C}
          produto={modal === "novo" ? null : modal}
          categorias={categorias}
          unidades={unidades}
          podeEditarLimites={perm.financeiro}
          onClose={() => setModal(null)}
          onSalvo={() => { setModal(null); recarregar(); }}
        />
      )}
    </>
  );
}

// ─── Categorias / Unidades (CRUD simples, só admin) ──────────────────────────

function ModalSimples({ C, titulo, campos, inicial, onClose, onSalvar }) {
  const [form, setForm] = useState(inicial);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (campos.some((c) => c.obrigatorio && !String(form[c.id] ?? "").trim())) {
      setErro("Preencha os campos obrigatórios.");
      return;
    }
    setSalvando(true);
    setErro("");
    try {
      await onSalvar(form);
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  return (
    <Modal
      C={C}
      titulo={titulo}
      onClose={onClose}
      largura={440}
      rodape={
        <>
          <button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button>
          <button onClick={salvar} disabled={salvando} style={{ ...btnStyle(C.brand), opacity: salvando ? 0.6 : 1 }}>{salvando ? "Salvando..." : "Salvar"}</button>
        </>
      }
    >
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {campos.map((c) => (
          <Campo key={c.id} label={c.label} C={C} obrigatorio={c.obrigatorio} ajuda={c.ajuda}>
            <input
              value={form[c.id] ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, [c.id]: c.mascara ? c.mascara(e.target.value) : e.target.value }))}
              inputMode={c.inputMode}
              maxLength={c.maxLength}
              style={inputStyle(C)}
            />
          </Campo>
        ))}
        {"ativo" in inicial && inicial.id && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={form.ativo} onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))} style={{ width: 18, height: 18 }} />
            Ativo
          </label>
        )}
      </div>
    </Modal>
  );
}

function AbaSimples({ C, isMobile, podeEditar, itens, colunas, campos, rotuloNovo, tituloItem, subtituloItem, detalhesItem, novoInicial, criar, atualizar, recarregar }) {
  const [modal, setModal] = useState(null);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8 }}>
        <span style={{ fontSize: 12, color: C.muted }}>{itens.length} registro(s){!podeEditar && " · somente leitura"}</span>
        {podeEditar && (
          <button onClick={() => setModal(novoInicial)} style={btnStyle(C.brand)}>
            <Plus size={15} /> {rotuloNovo}
          </button>
        )}
      </div>
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {itens.map((item) => (
            <CartaoMobile
              key={item.id}
              C={C}
              titulo={tituloItem(item)}
              subtitulo={subtituloItem && subtituloItem(item)}
              inativo={!item.ativo}
              detalhes={detalhesItem ? detalhesItem(item) : (!item.ativo && <BadgeAtivo ativo={false} C={C} />)}
              acao={podeEditar && <BotaoEditar C={C} onClick={() => setModal(item)} />}
            />
          ))}
        </div>
      ) : (
        <Tabela
          C={C}
          vazio="Nenhum registro."
          colunas={[...colunas, { id: "status", label: "Status" }, ...(podeEditar ? [{ id: "acoes", label: "", align: "right" }] : [])]}
          linhas={itens.map((item) => ({
            key: item.id,
            inativo: !item.ativo,
            ...Object.fromEntries(colunas.map((c) => [c.id, c.render ? c.render(item) : item[c.id]])),
            status: <BadgeAtivo ativo={item.ativo} C={C} />,
            acoes: <div style={{ display: "flex", justifyContent: "flex-end" }}><BotaoEditar C={C} onClick={() => setModal(item)} /></div>,
          }))}
        />
      )}
      {modal && (
        <ModalSimples
          C={C}
          titulo={modal.id ? "Editar" : rotuloNovo}
          campos={campos}
          inicial={modal}
          onClose={() => setModal(null)}
          onSalvar={async (form) => {
            if (form.id) await atualizar(form);
            else await criar(form);
            setModal(null);
            recarregar();
          }}
        />
      )}
    </>
  );
}

// ─── View ─────────────────────────────────────────────────────────────────────

export default function CadastrosEstoqueView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const perm = permissoesEstoque(getRoleFromToken());
  const [aba, setAba] = useState("produtos");
  const [dados, setDados] = useState({ produtos: [], categorias: [], unidades: [], fornecedores: [] });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setErro("");
    try {
      const [produtos, categorias, unidades, fornecedores] = await Promise.all([
        getEstoqueProdutos(), getEstoqueCategorias(), getEstoqueUnidades(), getEstoqueFornecedores(),
      ]);
      setDados({ produtos, categorias, unidades, fornecedores });
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <EstoqueLayout titulo="Cadastros do Estoque" isLight={isLight} setIsLight={setIsLight}>
      <div style={{ display: "flex", gap: 4, marginBottom: 16, overflowX: "auto", borderBottom: `1px solid ${C.border}` }}>
        {ABAS.map((a) => (
          <button
            key={a.id}
            onClick={() => setAba(a.id)}
            style={{
              background: "none", border: "none", borderBottom: `2px solid ${aba === a.id ? C.brand : "transparent"}`,
              color: aba === a.id ? C.brand : C.muted, fontWeight: aba === a.id ? 700 : 500, fontSize: 14,
              padding: "10px 14px", minHeight: 44, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
            }}
          >
            {a.label}
          </button>
        ))}
      </div>

      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      {carregando ? (
        <div style={{ color: C.muted, fontSize: 13, padding: 24, textAlign: "center" }}>Carregando...</div>
      ) : (
        <>
          {aba === "produtos" && (
            <AbaProdutos C={C} isMobile={isMobile} perm={perm} {...dados} recarregar={carregar} />
          )}
          {aba === "categorias" && (
            <AbaSimples
              C={C} isMobile={isMobile} podeEditar={perm.admin} itens={dados.categorias}
              colunas={[{ id: "nome", label: "Categoria" }, { id: "qtd", label: "Produtos ativos", align: "right", render: (c) => dados.produtos.filter((p) => p.categoria_id === c.id && p.ativo).length }]}
              campos={[{ id: "nome", label: "Nome", obrigatorio: true }]}
              rotuloNovo="Nova categoria" tituloItem={(c) => c.nome}
              novoInicial={{ nome: "", ativo: true }}
              criar={(f) => postEstoqueCategoria({ nome: f.nome.trim() })}
              atualizar={(f) => putEstoqueCategoria(f.id, { nome: f.nome.trim(), ativo: f.ativo })}
              recarregar={carregar}
            />
          )}
          {aba === "unidades" && (
            <AbaSimples
              C={C} isMobile={isMobile} podeEditar={perm.admin} itens={dados.unidades}
              colunas={[{ id: "sigla", label: "Sigla" }, { id: "descricao", label: "Descrição" }]}
              campos={[{ id: "sigla", label: "Sigla", obrigatorio: true, maxLength: 10 }, { id: "descricao", label: "Descrição", obrigatorio: true }]}
              rotuloNovo="Nova unidade" tituloItem={(u) => u.sigla} subtituloItem={(u) => u.descricao}
              novoInicial={{ sigla: "", descricao: "", ativo: true }}
              criar={(f) => postEstoqueUnidade({ sigla: f.sigla.trim(), descricao: f.descricao.trim() })}
              atualizar={(f) => putEstoqueUnidade(f.id, { sigla: f.sigla.trim(), descricao: f.descricao.trim(), ativo: f.ativo })}
              recarregar={carregar}
            />
          )}
          {aba === "fornecedores" && (
            <AbaSimples
              C={C} isMobile={isMobile} podeEditar={perm.financeiro}
              itens={dados.fornecedores.map((f) => ({ ...f, cnpj: formatCnpj(f.cnpj) }))}
              colunas={[{ id: "razao_social", label: "Razão social" }, { id: "cnpj", label: "CNPJ" }, { id: "contato", label: "Contato" }]}
              campos={[
                { id: "razao_social", label: "Razão social", obrigatorio: true },
                { id: "cnpj", label: "CNPJ", obrigatorio: true, inputMode: "numeric", mascara: mascaraCnpj, ajuda: "Os dígitos verificadores são conferidos." },
                { id: "contato", label: "Contato (telefone, e-mail, vendedor)" },
              ]}
              rotuloNovo="Novo fornecedor" tituloItem={(f) => f.razao_social} subtituloItem={(f) => f.cnpj}
              detalhesItem={(f) => (<>{f.contato && <span>{f.contato}</span>}{!f.ativo && <BadgeAtivo ativo={false} C={C} />}</>)}
              novoInicial={{ razao_social: "", cnpj: "", contato: "", ativo: true }}
              criar={(f) => postEstoqueFornecedor({ razao_social: f.razao_social.trim(), cnpj: f.cnpj, contato: f.contato?.trim() || null })}
              atualizar={(f) => putEstoqueFornecedor(f.id, { razao_social: f.razao_social.trim(), cnpj: f.cnpj, contato: f.contato?.trim() || null, ativo: f.ativo })}
              recarregar={carregar}
            />
          )}
        </>
      )}
    </EstoqueLayout>
  );
}
